import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { Fieldset } from 'primereact/fieldset';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_PATH || 'https://localhost:5001/api/';

const SmsPaymentManager = () => {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sendSmsOnly, setSendSmsOnly] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    payDate: '',
    pastDue: null,
    memberId: '',
    memberName: '',
    mobileNo: '',
    fromDate: '',
    toDate: '',
    minPayment: null,
    maxPayment: null,
    page: 1,
    pageSize: 50
  });

  // Search records
  const searchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}SmsPayment/filter`, filters);
      console.log('API Response:', response.data); // Debug log
      setRecords(response.data.records || []);
      setTotalRecords(response.data.total || 0);
      setSelectedRecords([]);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: `Found ${response.data.total} records`
      });
    } catch (error) {
      console.error('Search error:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.error || 'Failed to fetch records'
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate and send SMS
  const generateAndSendSms = async () => {
    if (selectedRecords.length === 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select records to process'
      });
      return;
    }

    confirmDialog({
      message: `Generate payment links and send SMS to ${selectedRecords.length} customers?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await processSms(selectedRecords);
      }
    });
  };

  // Process SMS
  const processSms = async (recordsToProcess) => {
    setProcessing(true);
    setProgress(0);

    try {
      const request = {
        records: recordsToProcess.map(r => ({
          billNo: r.billNo,
          loanAccountNo: r.loanAccountNo,
          memberName: r.memberName,
          mobileNo: r.mobileNo || '7777777777',
          payment: r.payment,
          pastDue: r.pastDue,
          payDate: r.payDate,
          billDate: r.billDate,
          ledgerBalance: r.ledgerBalance
        })),
        sendSmsOnly: sendSmsOnly
      };

      // Simulate progress
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + Math.random() * 3;
          return newProgress > 95 ? 95 : newProgress;
        });
      }, 500);

      const response = await axios.post(`${API_BASE_URL}SmsPayment/generate-and-send`, request);
      
      clearInterval(interval);
      setProgress(100);
      setResults(response.data);
      setShowResults(true);

      toast.current.show({
        severity: 'success',
        summary: 'Complete',
        detail: `Processed ${response.data.total} records`
      });
    } catch (error) {
      console.error('Process error:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.error || 'Failed to process'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Resend SMS
  const resendSms = async () => {
    if (selectedRecords.length === 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select records to resend SMS'
      });
      return;
    }

    confirmDialog({
      message: `Resend SMS to ${selectedRecords.length} customers?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const request = {
            records: selectedRecords.map(r => ({
              billNo: r.billNo,
              mobileNo: r.mobileNo,
              paymentUrl: r.paymentUrl || `https://payment.example.com/pay/${r.billNo}`,
              memberName: r.memberName,
              payment: r.payment,
              payDate: r.payDate
            }))
          };

          const response = await axios.post(`${API_BASE_URL}SmsPayment/resend-sms`, request);
          
          toast.current.show({
            severity: 'success',
            summary: 'Complete',
            detail: `Resent SMS to ${response.data.success} customers`
          });
        } catch (error) {
          console.error('Resend error:', error);
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.error || 'Failed to resend SMS'
          });
        }
      }
    });
  };

  // Export results
  const exportResults = () => {
    if (!results || !results.details) return;

    const csv = [
      ['Bill No', 'Payment Success', 'SMS Success', 'Mobile', 'Payment URL', 'SMS Message', 'Error'].join(','),
      ...results.details.map(r => [
        r.billNo || '',
        r.payment?.success ? 'Yes' : 'No',
        r.sms?.success ? 'Yes' : 'No',
        r.sms?.mobileNo || '',
        r.payment?.paymentUrl || '',
        r.sms?.message || '',
        r.sms?.error || r.payment?.error || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sms_payment_results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Process all filtered records
  const processAll = async () => {
    if (records.length === 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No records to process. Please search first.'
      });
      return;
    }

    confirmDialog({
      message: `Process all ${records.length} records?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        setSelectedRecords(records);
        await processSms(records);
      }
    });
  };

  // Left toolbar template
  const leftToolbarTemplate = () => (
    <div className="flex flex-wrap gap-2">
      <Button 
        label="Generate & Send SMS" 
        icon="pi pi-send" 
        onClick={generateAndSendSms}
        disabled={selectedRecords.length === 0 || processing}
        className="p-button-success"
      />
      <Button 
        label="Resend SMS" 
        icon="pi pi-refresh" 
        onClick={resendSms}
        disabled={selectedRecords.length === 0 || processing}
        className="p-button-warning"
      />
      <Button 
        label="Process All" 
        icon="pi pi-play-circle" 
        onClick={processAll}
        disabled={records.length === 0 || processing}
        className="p-button-secondary"
      />
      {results && (
        <Button 
          label="Export Results" 
          icon="pi pi-download" 
          onClick={exportResults}
          className="p-button-help"
        />
      )}
    </div>
  );

  // Right toolbar template
  const rightToolbarTemplate = () => (
    <div className="flex align-items-center gap-2">
      <Badge value={`${selectedRecords.length} selected`} />
      <Badge value={`${records.length} records`} severity="info" />
    </div>
  );

  // Table templates
  const statusBody = (rowData) => {
    if (rowData.smsSent) {
      return <Tag severity="success" value="SMS Sent" />;
    }
    return <Tag severity="info" value="Pending" />;
  };

  const currencyBody = (rowData) => {
    return `₹${rowData.payment?.toFixed(2) || '0.00'}`;
  };

  // FIXED: Date formatting function with proper null checks
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    
    try {
      // If it's a string, try to parse it
      let date;
      if (typeof dateValue === 'string') {
        // Check if it's an ISO string or date string
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return '-';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      // Format as DD/MM/YYYY
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return '-';
    }
  };

  // FIXED: Date body template with proper formatting
  const dateBody = (rowData, field) => {
    const value = rowData[field];
    return formatDate(value);
  };

  // Load initial records
  useEffect(() => {
    searchRecords();
  }, []);

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Card title="SMS Payment Manager">
        <div className="grid">
          {/* Filter Section */}
          <div className="col-12">
            <Fieldset legend="Filter Records" toggleable>
              <div className="grid p-fluid">
                <div className="col-12 md:col-2">
                  <label>Pay Date</label>
                  <InputText 
                    type="date"
                    value={filters.payDate} 
                    onChange={(e) => setFilters({...filters, payDate: e.target.value})}
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Past Due</label>
                  <InputText 
                    value={filters.pastDue} 
                    onChange={(e) => setFilters({...filters, pastDue: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Member ID</label>
                  <InputText 
                    value={filters.memberId} 
                    onChange={(e) => setFilters({...filters, memberId: e.target.value})}
                    placeholder="Member ID"
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Member Name</label>
                  <InputText 
                    value={filters.memberName} 
                    onChange={(e) => setFilters({...filters, memberName: e.target.value})}
                    placeholder="Search name..."
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Mobile No</label>
                  <InputText 
                    value={filters.mobileNo} 
                    onChange={(e) => setFilters({...filters, mobileNo: e.target.value})}
                    placeholder="Mobile"
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Page Size</label>
                  <select 
                    className="w-full p-2 border rounded"
                    value={filters.pageSize}
                    onChange={(e) => setFilters({...filters, pageSize: parseInt(e.target.value)})}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                </div>
                <div className="col-12 md:col-2">
                  <label>From Date</label>
                  <InputText 
                    type="date"
                    value={filters.fromDate} 
                    onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>To Date</label>
                  <InputText 
                    type="date"
                    value={filters.toDate} 
                    onChange={(e) => setFilters({...filters, toDate: e.target.value})}
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Min Payment</label>
                  <InputText 
                    value={filters.minPayment} 
                    onChange={(e) => setFilters({...filters, minPayment: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label>Max Payment</label>
                  <InputText 
                    value={filters.maxPayment} 
                    onChange={(e) => setFilters({...filters, maxPayment: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-12 md:col-2 flex align-items-end">
                  <Button 
                    label="Search" 
                    icon="pi pi-search" 
                    onClick={searchRecords}
                    loading={loading}
                    className="w-full"
                  />
                </div>
              </div>
            </Fieldset>
          </div>

          {/* SMS Only Mode Toggle */}
          <div className="col-12 mt-2">
            <div className="flex align-items-center">
              <input 
                type="checkbox" 
                id="smsOnly"
                checked={sendSmsOnly} 
                onChange={(e) => setSendSmsOnly(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="smsOnly">SMS Only Mode (Skip Payment Generation)</label>
            </div>
          </div>

          {/* Actions Toolbar */}
          <div className="col-12 mt-3">
            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {processing && (
              <div className="mt-3">
                <ProgressBar value={progress} />
                <div className="text-center mt-2">
                  <small>Processing... {Math.round(progress)}%</small>
                </div>
              </div>
            )}
          </div>

          {/* Records Table */}
          <div className="col-12 mt-3">
            <DataTable 
              value={records} 
              selection={selectedRecords}
              onSelectionChange={(e) => setSelectedRecords(e.value)}
              dataKey="billNo"
              loading={loading}
              paginator
              rows={filters.pageSize}
              totalRecords={totalRecords}
              lazy
              onPage={(e) => setFilters({...filters, page: e.page + 1, pageSize: e.rows})}
              scrollable
              scrollHeight="400px"
              resizableColumns
              columnResizeMode="fit"
              emptyMessage="No records found"
            >
              <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
              <Column field="billNo" header="Bill No" sortable />
              <Column field="loanAccountNo" header="Account No" sortable />
              <Column field="memberName" header="Member Name" sortable />
              <Column field="mobileNo" header="Mobile" sortable />
              <Column field="payment" header="Payment" body={currencyBody} sortable />
              <Column field="pastDue" header="Past Due" body={currencyBody} sortable />
              <Column field="ledgerBalance" header="Balance" body={currencyBody} sortable />
              <Column field="billDate" header="Bill Date" body={(row) => dateBody(row, 'billDate')} sortable />
              <Column field="payDate" header="Pay Date" body={(row) => dateBody(row, 'payDate')} sortable />
              <Column field="smsSent" header="Status" body={statusBody} />
            </DataTable>
          </div>
        </div>
      </Card>

      {/* Results Dialog */}
      <Dialog 
        header="Processing Results" 
        visible={showResults} 
        style={{ width: '90vw', maxWidth: '1200px' }}
        onHide={() => setShowResults(false)}
        maximizable
      >
        {results && (
          <div>
            <div className="grid">
              <div className="col-12 md:col-3">
                <Card className="summary-card">
                  <h6>Total</h6>
                  <div className="stats-number">{results.total}</div>
                </Card>
              </div>
              <div className="col-12 md:col-3">
                <Card className="summary-card" style={{ borderLeftColor: '#28a745' }}>
                  <h6>Payment Success</h6>
                  <div className="stats-number" style={{ color: '#28a745' }}>
                    {results.paymentSuccess || 0}
                  </div>
                </Card>
              </div>
              <div className="col-12 md:col-3">
                <Card className="summary-card" style={{ borderLeftColor: '#28a745' }}>
                  <h6>SMS Success</h6>
                  <div className="stats-number" style={{ color: '#28a745' }}>
                    {results.smsSuccess || 0}
                  </div>
                </Card>
              </div>
              <div className="col-12 md:col-3">
                <Card className="summary-card">
                  <h6>Time</h6>
                  <div className="stats-number" style={{ fontSize: '1.5rem' }}>
                    {results.processingTime?.toFixed(1) || 0}s
                  </div>
                </Card>
              </div>
            </div>

            <div className="mt-3">
              <DataTable value={results.details || []} className="p-datatable-sm">
                <Column field="billNo" header="Bill No" />
                <Column field="payment.success" header="Payment" body={(row) => (
                  <Tag severity={row.payment?.success ? 'success' : 'danger'} 
                       value={row.payment?.success ? 'Success' : 'Failed'} />
                )} />
                <Column field="sms.success" header="SMS" body={(row) => (
                  <Tag severity={row.sms?.success ? 'success' : 'danger'} 
                       value={row.sms?.success ? 'Sent' : 'Failed'} />
                )} />
                <Column field="sms.mobileNo" header="Mobile" />
                <Column field="payment.paymentUrl" header="Payment URL" 
                  body={(row) => row.payment?.paymentUrl ? (
                    <a href={row.payment.paymentUrl} target="_blank" rel="noopener noreferrer">
                      <i className="pi pi-external-link"></i> Link
                    </a>
                  ) : '-'}
                />
                <Column field="sms.message" header="SMS Status" />
                <Column field="sms.error || payment.error" header="Error" 
                  body={(row) => row.sms?.error || row.payment?.error || '-'} />
              </DataTable>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default SmsPaymentManager;